import { useState, useEffect } from 'react';

/**
 * A React hook for reading and writing a value to localStorage, with built-in state synchronization.
 *
 * Persists a value in the browser's localStorage under the given key and keeps it in sync with React state.
 * When initialized, it tries to read the value from localStorage, or uses the provided initial value if not present.
 * The value is automatically updated in localStorage whenever it changes.
 *
 * The key must stay constant for the component's lifetime: the stored value is only read on mount,
 * and a key change makes the write effect copy the previous key's value into the new key. For keys
 * derived from user/house ids, use `useScopedLocalStorage` instead.
 *
 * @template T The type of the value being stored.
 * @param {string} key - The key under which the value will be stored in localStorage.
 * @param {T} initialValue - The initial value to use if none is found in localStorage.
 * @returns {[T, (value: T) => void]} A tuple with the current value and a setter function.
 *
 * @example
 * const [user, setUser] = useLocalStorage<User>('user', defaultUser);
 *
 * // Updates both React state and localStorage
 * setUser({ id: 1, name: "Alice" });
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
	const [value, setValue] = useState<T>(() => {
		if (typeof window === 'undefined') return initialValue;
		try {
			const item = window.localStorage.getItem(key);
			return item ? JSON.parse(item) : initialValue;
		} catch {
			return initialValue;
		}
	});

	useEffect(() => {
		if (typeof window === 'undefined') return;
		try {
			window.localStorage.setItem(key, JSON.stringify(value));
		} catch {
			console.error(`Error saving to localStorage for key "${key}":`, value);
		}
	}, [key, value]);

	return [value, setValue] as const;
}
